// Platform rail configuration — admin-only.
//
// GET /v1/payout-rails lists, per provider, the amount band the rail
// physically accepts (rail_min / rail_max) and its service window.
// PATCH /v1/payout-rails/:provider updates one. Both take effect on the
// next payout: the backend reads them per request, no cache.
//
// Money is bigint paise (CLAUDE.md rule 6).
import { apiClient } from "./client";

interface RawPayoutRail {
  provider: string;
  rail_min_minor: number | bigint | null;
  rail_max_minor: number | bigint | null;
  service_window_opens_at: string | null;
  service_window_closes_at: string | null;
  service_window_days: string[];
  service_window_timezone: string;
  configured: boolean;
}

export interface PayoutRail {
  /** Public provider token ("idfc", "buckzy", "manual", …) — pass back on PATCH. */
  provider: string;
  railMinMinor: bigint | null;
  railMaxMinor: bigint | null;
  serviceWindowOpensAt: string | null;
  serviceWindowClosesAt: string | null;
  serviceWindowDays: string[];
  serviceWindowTimezone: string;
  /** false = no platform row yet; values shown are compiled defaults. */
  configured: boolean;
}

function fromRaw(r: RawPayoutRail): PayoutRail {
  return {
    provider: r.provider,
    railMinMinor: r.rail_min_minor == null ? null : BigInt(r.rail_min_minor),
    railMaxMinor: r.rail_max_minor == null ? null : BigInt(r.rail_max_minor),
    serviceWindowOpensAt: r.service_window_opens_at,
    serviceWindowClosesAt: r.service_window_closes_at,
    serviceWindowDays: r.service_window_days ?? [],
    serviceWindowTimezone: r.service_window_timezone,
    configured: r.configured,
  };
}

export async function listPayoutRails(): Promise<PayoutRail[]> {
  const resp = await apiClient.get<{ rails: RawPayoutRail[] }>(
    "/v1/payout-rails",
  );
  return (resp.data.rails ?? []).map(fromRaw);
}

export interface UpdatePayoutRailInput {
  railMinMinor?: bigint;
  railMaxMinor?: bigint;
  clearRailMin?: boolean;
  clearRailMax?: boolean;
}

export async function updatePayoutRail(
  provider: string,
  input: UpdatePayoutRailInput,
): Promise<PayoutRail> {
  const body: Record<string, unknown> = {};
  if (input.railMinMinor !== undefined)
    body.rail_min_minor = input.railMinMinor;
  if (input.railMaxMinor !== undefined)
    body.rail_max_minor = input.railMaxMinor;
  if (input.clearRailMin) body.clear_rail_min = true;
  if (input.clearRailMax) body.clear_rail_max = true;
  const resp = await apiClient.patch<RawPayoutRail>(
    `/v1/payout-rails/${encodeURIComponent(provider)}`,
    body,
  );
  return fromRaw(resp.data);
}
